// Chunked CSV serialization for the table exports.
//
// react-csv's <CSVLink> serializes the whole dataset inside render(), builds a
// Blob AND an unused data: URI from it, and never revokes the object URL. At
// 84k rows that is three copies of a ~135MB string per render. These helpers do
// the same serialization, but once, on demand, streaming into Blob parts.
//
// Output is intentionally byte-identical to react-csv's `joiner()` so existing
// exports do not change shape, and csv.test.ts asserts that against react-csv
// directly. Carried over with it: neither implementation doubles an enclosing
// character appearing inside a value, so a field containing a quote still
// produces malformed CSV. Fixing that changes every export's output, so it is
// left as a deliberate follow-up rather than folded into a performance change.

const ROWS_PER_CHUNK = 500;

export interface CsvOptions {
  separator?: string;
  enclosingCharacter?: string;
  uFEFF?: boolean;
}

const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

/**
 * Serialize one row the way react-csv does: null/undefined become empty, every
 * cell is wrapped in the enclosing character, cells joined by the separator.
 */
function serializeRow(row: unknown[], separator: string, enclosingCharacter: string): string {
  let line = '';
  for (let i = 0; i < row.length; i += 1) {
    const cell = row[i];
    if (i > 0) line += separator;
    line += enclosingCharacter + (cell === undefined || cell === null ? '' : cell) + enclosingCharacter;
  }
  return line;
}

/**
 * Turn an array of rows into Blob parts, serializing ROWS_PER_CHUNK rows at a
 * time so the engine never has to hold the entire CSV as one contiguous string.
 *
 * @param {unknown[][]} data rows to serialize
 * @param {CsvOptions} options separator / enclosing character / BOM
 * @returns {string[]} Blob parts
 */
export function buildCsvChunks(data: unknown[][], options: CsvOptions = {}): string[] {
  const { separator = ',', enclosingCharacter = '"', uFEFF = false } = options;
  const chunks: string[] = [];
  if (uFEFF) chunks.push('﻿');

  // Matches react-csv: falsy rows are dropped, empty arrays are kept.
  const rows = data.filter((row) => row) as unknown[][];

  let buffer: string[] = [];
  for (let i = 0; i < rows.length; i += 1) {
    buffer.push(serializeRow(rows[i], separator, enclosingCharacter));
    if (buffer.length >= ROWS_PER_CHUNK) {
      chunks.push(buffer.join('\n'));
      buffer = [];
      // Rows are joined by "\n" across chunk boundaries too.
      if (i < rows.length - 1) chunks.push('\n');
    }
  }
  if (buffer.length > 0) chunks.push(buffer.join('\n'));

  return chunks;
}

/**
 * Build a downloadable CSV Blob from an array of rows.
 */
export function buildCsvBlob(data: unknown[][], options: CsvOptions = {}): Blob {
  const type = isSafari() ? 'application/csv' : 'text/csv';
  return new Blob(buildCsvChunks(data, options), { type: `${type};charset=utf-8` });
}

/**
 * Hand a Blob to the browser as a file download, then release the object URL so
 * a 100MB+ export is not pinned in memory for the rest of the session.
 */
export function downloadBlob(blob: Blob, fileName: string) {
  const nav = window.navigator as Navigator & { msSaveBlob?: (b: Blob, n: string) => void };
  if (typeof nav.msSaveBlob === 'function') {
    nav.msSaveBlob(blob, fileName);
    return;
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Safari needs the URL to outlive the click by a tick.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
